import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Search, Edit, Trash2, UserCheck, UserX, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { t } = useLanguage();
  const { isHR } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const params = {};
        if (statusFilter !== 'all') {
          params.is_active = statusFilter === 'active';
        }
        const response = await axios.get(`${API}/employees`, { params });
        setEmployees(response.data);
      } catch (error) {
        console.error('Error fetching employees:', error);
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [statusFilter, t]);

  const refetchEmployees = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.is_active = statusFilter === 'active';
      }
      const response = await axios.get(`${API}/employees`, { params });
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success(t('deletedSuccessfully'));
      refetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error(t('errorOccurred'));
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const searchLower = searchTerm.toLowerCase();
    return (
      emp.first_name.toLowerCase().includes(searchLower) ||
      emp.last_name.toLowerCase().includes(searchLower) ||
      emp.employee_id.toLowerCase().includes(searchLower) ||
      emp.email?.toLowerCase().includes(searchLower)
    );
  });

  const getContractTypeLabel = (type) => {
    const types = {
      vollzeit: t('fulltime'),
      teilzeit: t('parttime'),
      freelancer: t('freelancer'),
      minijob: t('minijob'),
      befristet: t('temporary')
    };
    return types[type] || type;
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/employees/excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `Mitarbeiter_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('exportSuccess') || 'Export erfolgreich');
    } catch (error) {
      console.error('Error exporting employees:', error);
      toast.error(t('exportError') || 'Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('employees')}</h1>
        <div className="flex gap-2">
          <Button
            onClick={handleExportExcel}
            disabled={exporting || employees.length === 0}
            variant="outline"
            className="border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24] hover:text-white"
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exportiere...' : 'Excel Export'}
          </Button>
          {isHR() && (
            <Button
              onClick={() => navigate('/employees/new')}
              className="bg-[#E31E24] hover:bg-[#C41218] text-white btn-primary-glow"
              data-testid="add-employee-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('newEmployee')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white border-gray-200 text-[#1A1A1A]"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger 
                className="w-full sm:w-48 bg-white border-gray-200 text-[#1A1A1A]"
                data-testid="status-filter"
              >
                <SelectValue placeholder={t('filter')} />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">{t('filter')}: All</SelectItem>
                <SelectItem value="active">{t('active')}</SelectItem>
                <SelectItem value="inactive">{t('inactive')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead className="table-header">{t('employeeId')}</TableHead>
                  <TableHead className="table-header">{t('firstName')}</TableHead>
                  <TableHead className="table-header">{t('lastName')}</TableHead>
                  <TableHead className="table-header">{t('contractType')}</TableHead>
                  <TableHead className="table-header">{t('vacationDaysRemaining')}</TableHead>
                  <TableHead className="table-header">{t('status')}</TableHead>
                  <TableHead className="table-header text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-400">
                      {t('noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((emp) => (
                    <TableRow 
                      key={emp.id} 
                      className="table-row cursor-pointer"
                      onClick={() => navigate(`/employees/${emp.id}`)}
                      data-testid={`employee-row-${emp.id}`}
                    >
                      <TableCell className="font-mono text-gray-500">{emp.employee_number || '-'}</TableCell>
                      <TableCell className="text-[#1A1A1A]">{emp.first_name}</TableCell>
                      <TableCell className="text-[#1A1A1A]">{emp.last_name}</TableCell>
                      <TableCell className="text-gray-500">{getContractTypeLabel(emp.contract_type)}</TableCell>
                      <TableCell className="font-mono">
                        <span className={emp.vacation_days_remaining < 0 ? 'text-red-400' : 'text-[#1A1A1A]'}>
                          {emp.vacation_days_remaining}
                        </span>
                      </TableCell>
                      <TableCell>
                        {emp.is_active ? (
                          <Badge className="badge-success">
                            <UserCheck className="w-3 h-3 mr-1" />
                            {t('active')}
                          </Badge>
                        ) : (
                          <Badge className="badge-error">
                            <UserX className="w-3 h-3 mr-1" />
                            {t('inactive')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/employees/${emp.id}`)}
                            className="text-gray-500 hover:text-[#1A1A1A]"
                            data-testid={`view-employee-${emp.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isHR() && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/employees/${emp.id}/edit`)}
                                className="text-gray-500 hover:text-[#1A1A1A]"
                                data-testid={`edit-employee-${emp.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(emp.id)}
                                className="text-gray-500 hover:text-[#E31E24]"
                                data-testid={`delete-employee-${emp.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
